import type { CouplingRule } from '@/engine/dynamics';

/**
 * 风电机组 动力学耦合规则
 *
 * 物理逻辑：
 *  1. 风速 → 载荷/功率（风速 3~12m/s 区间功率随风速立方增长）
 *  2. 桨距角 ↑ → 转速 ↓（桨距角增大减少迎风面积）
 *  3. 转速 → 发电功率（转速影响发电机输出）
 *  4. 齿轮箱油温 ↑ → 振动 ↑（高温→粘度降→润滑恶化）
 *  5. 偏航偏离 → 功率 ↓（功率按 cos(θ) 衰减）
 *  6. 载荷 → 齿轮箱油温 ↑（负载大摩擦热多）
 *  7. 轮毂振动 → 塔筒振动（振动传递）
 *  8. 功率 → 发电机绕组温度 ↑（电流热效应）
 *  9. 风速 → 转速（风能驱动叶轮）
 * 10. 齿轮箱油温 → 发电机绕组温度（热传导）
 */
export const windturbineCouplings: CouplingRule[] = [
  // 1. 风速 → 载荷/功率（非线性：风速在 3~12m/s 功率随风速立方增长，这里用线性近似）
  {
    from: { equipmentId: 'WIND', param: 'WIND-SPD' },
    to: { equipmentId: 'HUB', param: 'LOAD' },
    gain: 0.15,      // 风速 +1m/s → 载荷 +0.15 MW（在 3~15m/s 区间近似）
    baseline: 12,
    tau: 10,
  },

  // 2. 桨距角 A → 转速（桨距角 ↑ → 转速 ↓，负耦合）
  {
    from: { equipmentId: 'BLADE-A', param: 'PITCH-A' },
    to: { equipmentId: 'BLADE-A', param: 'RPM' },
    gain: -0.25,     // 桨距角 +1° → 转速 -0.25 rpm
    baseline: 8,
    tau: 6,
  },

  // 3. 桨距角 B → 转速
  {
    from: { equipmentId: 'BLADE-B', param: 'PITCH-B' },
    to: { equipmentId: 'BLADE-B', param: 'RPM' },
    gain: -0.25,
    baseline: 8,
    tau: 6,
  },

  // 4. 桨距角 C → 转速
  {
    from: { equipmentId: 'BLADE-C', param: 'PITCH-C' },
    to: { equipmentId: 'BLADE-C', param: 'RPM' },
    gain: -0.25,
    baseline: 8,
    tau: 6,
  },

  // 5. 转速 → 发电功率（转速 ↑ → 功率 ↑）
  {
    from: { equipmentId: 'BLADE-A', param: 'RPM' },
    to: { equipmentId: 'GEN', param: 'POWER' },
    gain: 0.12,      // 转速 +1 rpm → 功率 +0.12 MW
    baseline: 15,
    tau: 8,
  },

  // 6. 齿轮箱油温 → 齿轮箱振动（高温 → 粘度降 → 振动升，正耦合）
  {
    from: { equipmentId: 'GEARBOX', param: 'T-GB' },
    to: { equipmentId: 'GEARBOX', param: 'VIB-GB' },
    gain: 0.08,      // 油温 +10°C → 振动 +0.8 mm/s
    baseline: 72,
    tau: 12,
  },

  // 7. 偏航角度偏离 → 功率下降（偏航 ↑ → 功率 ↓，负耦合，按 cos(θ) 近似）
  {
    from: { equipmentId: 'YAW', param: 'YAW-POS' },
    to: { equipmentId: 'GEN', param: 'POWER' },
    gain: -0.03,     // 偏航 +1° → 功率 -0.03 MW（小角度近似 cos(θ)≈1-θ²/2）
    baseline: 0,
    tau: 10,
  },

  // 8. 载荷 → 齿轮箱油温（负载大 → 摩擦热多 → 油温升）
  {
    from: { equipmentId: 'HUB', param: 'LOAD' },
    to: { equipmentId: 'GEARBOX', param: 'T-GB' },
    gain: 4,         // 载荷 +1 MW → 油温 +4°C
    baseline: 2.5,
    tau: 18,
  },

  // 9. 轮毂振动 → 塔筒振动（振动传递）
  {
    from: { equipmentId: 'HUB', param: 'VIB-HUB' },
    to: { equipmentId: 'SENSOR-VIB', param: 'VIB-TOWER' },
    gain: 0.4,       // 轮毂振动 +1 mm/s → 塔筒振动 +0.4 mm/s
    baseline: 2.8,
    tau: 3,
  },

  // 10. 功率 → 发电机绕组温度（电流热效应）
  {
    from: { equipmentId: 'GEN', param: 'POWER' },
    to: { equipmentId: 'GEN', param: 'T-GEN' },
    gain: 12,        // 功率 +1 MW → 绕组温度 +12°C
    baseline: 2.5,
    tau: 15,
  },
];
